import { useColorMode, useColorModeValue, useToast } from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { darkenColor } from './utils';

import type { IToastOptions } from './types';

export const useCustomToast = () => {
	const toast = useToast();

	const showToast = useCallback(
		({ title, description, duration, isClosable, position, status = 'success' }: IToastOptions) => {
			toast({
				title: title,
				description: description,
				duration: duration ?? 9000,
				isClosable: isClosable ?? true,
				position: position ?? 'bottom-right',
				status,
			});
		},
		[toast]
	);

	return { showToast };
};

export const useIconColor = () => {
	return useColorModeValue('black', 'white');
};

export const useAutoContrastColor = () => {
	const ref = useRef(null);
	const [color, setColor] = useState('black');
	const { colorMode } = useColorMode();
	// Helper to find nearest non-transparent background
	const getBackgroundColor = (reactNode) => {
		let node = reactNode;
		while (node) {
			const bg = window.getComputedStyle(node).backgroundColor;
			if (bg && !bg.includes('rgba(0, 0, 0, 0)') && !bg.includes('transparent')) {
				return bg;
			}
			node = node.parentElement;
		}
		return 'rgb(255, 255, 255)'; // fallback: white
	};

	useEffect(() => {
		if (ref.current) {
			const bg = getBackgroundColor(ref.current);
			const rgb = bg.match(/\d+/g)?.map(Number);
			if (rgb && rgb.length >= 3) {
				const [r, g, b] = rgb;
				//Source: https://www.w3.org/TR/AERT/#color-contrast
				const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
				setColor(luminance > 0.6 ? 'black' : 'white');
			}
		}
	}, [colorMode]);

	return { color, ref };
};

export const useDarkenInteractionStyles = (backgroundColor: string, hoverDarken: number, activeDarken: number) => {
	const hoverStyle = useMemo(() => {
		return {
			backgroundColor: darkenColor(backgroundColor, hoverDarken),
		};
	}, [backgroundColor, hoverDarken]);

	const activeStyle = useMemo(() => {
		return {
			backgroundColor: darkenColor(backgroundColor, activeDarken),
		};
	}, [backgroundColor, activeDarken]);

	return { hoverStyle, activeStyle };
};
